// src/components/sessions/SessionChatModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon, 
  ChatBubbleLeftRightIcon, 
  UserCircleIcon,
  DocumentArrowDownIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import sessionService from '@/services/sessionService'; 

const SessionChatModal = ({ isOpen, sessionId, sessionTitle, onClose }) => {
  const { t, i18n } = useTranslation();
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  useEffect(() => {
    if (isOpen && sessionId) {
      loadChatHistory();
    } else {
      setChatMessages([]);
      setOffset(0);
      setHasMore(false);
      setError('');
      setLoading(true);
    }
  }, [isOpen, sessionId]);

  const loadChatHistory = async (loadMore = false) => {
    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const response = await sessionService.getSessionChatHistory(
        sessionId, 
        { limit, offset: loadMore ? offset : 0 }
      );
      
      if (response.success && response.data) {
        const formattedMessages = response.data.map(msg => ({
          id: msg.id,
          sender: {
            id: msg.sender_user_id,
            name: msg.sender_name || t('chat.unknownUser'),
            institutionMemberId: msg.sender_im_id,
            isTeacher: msg.sender_role === 'teacher'
          },
          content: msg.message_content,
          timestamp: new Date(msg.sent_at),
          attachment: msg.attachment_url ? {
            name: msg.attachment_name,
            type: msg.attachment_type,
            size: msg.attachment_size_kb,
            url: msg.attachment_url
          } : null
        }));
        
        if (loadMore) {
          setChatMessages(prev => [...prev, ...formattedMessages]);
          setOffset(prev => prev + limit);
        } else {
          setChatMessages(formattedMessages);
          setOffset(limit);
        }
        
        setHasMore(response.pagination?.hasMore || false);
      }
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError(t('chat.errors.loadFailed'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const groupedMessages = chatMessages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const detectAndFormatLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline inline-flex items-center gap-1"
          >
            <LinkIcon className="w-3 h-3" />
            {part.length > 50 ? part.substring(0, 50) + '...' : part}
          </a>
        );
      }
      return part;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-cyan-600" />
            <div>
              <h2 className="text-xl font-bold text-slate-800">{t('chat.transcriptTitle')}</h2>
              <p className="text-sm text-slate-600">{sessionTitle}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-2"></div>
              <p className="text-slate-600">{t('chat.loadingHistory')}</p>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {hasMore && (
                <div className="text-center mb-4">
                  <button
                    onClick={() => loadChatHistory(true)}
                    disabled={loadingMore}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors"
                  >
                    {loadingMore ? t('common.loading') : t('chat.loadEarlier')}
                  </button>
                </div>
              )}

              {Object.entries(groupedMessages).map(([date, messages]) => (
                <div key={date}>
                  <div className="text-center my-4">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {date}
                    </span>
                  </div>
                  
                  {messages.map((message) => (
                    <div key={message.id} className="flex items-start space-x-3 mb-4">
                      <div className="flex-shrink-0">
                        <UserCircleIcon className="w-8 h-8 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <span className="font-medium text-slate-800">
                            {message.sender.name}
                          </span>
                          {message.sender.isTeacher && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                              {t('roles.teacher')}
                            </span>
                          )}
                          <span className="text-xs text-slate-500">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                        
                        <p className="text-slate-700 whitespace-pre-wrap break-words">
                          {detectAndFormatLinks(message.content)}
                        </p>
                        
                        {message.attachment && (
                          <div className="mt-2">
                            <a
                              href={message.attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors"
                            >
                              <DocumentArrowDownIcon className="w-4 h-4" />
                              <span>{message.attachment.name}</span>
                              {message.attachment.size && (
                                <span className="text-xs text-slate-500">
                                  ({Math.round(message.attachment.size / 1024)}KB)
                                </span>
                              )}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {chatMessages.length === 0 && !loading && (
                <div className="text-center py-8 text-slate-500">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('chat.noMessages')}</p>
                </div>
              )}
            </>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500 text-center">
            {t('chat.readOnlyNotice')} • {t('chat.totalMessages', { count: chatMessages.length })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionChatModal;